import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { Editor } from "@tiptap/react";

export type DraftSaveState = "idle" | "saving" | "saved" | "error";

type UseDraftAutosaveProps = {
  enabled: boolean;
  //builds the draft payload from the current form/editor state and persists it
  persist: (editorHtml: string | null) => Promise<void> | void;
  //TipTap editor whose content is part of the draft but lives outside the form state
  editor?: Editor | null;
  debounceMs?: number;
};

//debounced draft autosave, extracted from the DocumentEditorPanel pattern
const useDraftAutosave = ({ enabled, persist, editor = null, debounceMs = 750 }: UseDraftAutosaveProps) => {
  const [saveState, setSaveState] = useState<DraftSaveState>("idle");
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const editorHtmlRef = useRef<string | null>(null);
  const hasSaveErrorRef = useRef(false);

  const debouncedPersistRef = useRef(
    _.debounce(() => {
      const persist = persistRef.current;
      const editorHtml = editorHtmlRef.current;
      //chain saves so a slow PATCH cannot overtake a newer one
      saveQueueRef.current = saveQueueRef.current
        .catch(() => null)
        .then(async () => {
          await persist(editorHtml);
        });
      saveQueueRef.current.then(
        () => {
          hasSaveErrorRef.current = false;
          setSaveState("saved");
        },
        () => {
          hasSaveErrorRef.current = true;
          setSaveState("error");
        },
      );
    }, debounceMs)
  );

  const scheduleSave = () => {
    if (!enabled) {
      return;
    }
    editorHtmlRef.current = editor?.getHTML() ?? null;
    setSaveState("saving");
    debouncedPersistRef.current();
  };

  const flushSave = async () => {
    // A later close/execute attempt retries a failed save without requiring another edit.
    if (hasSaveErrorRef.current) {
      debouncedPersistRef.current();
    }
    let pendingSave;
    do {
      debouncedPersistRef.current.flush();
      pendingSave = saveQueueRef.current;
      await pendingSave;
      debouncedPersistRef.current.flush();
    } while (pendingSave !== saveQueueRef.current);
  };

  useEffect(() => {
    //flush a pending save on unmount so edits are not lost when the drawer closes
    return () => {
      debouncedPersistRef.current.flush();
    };
  }, []);

  useEffect(() => {
    if (!editor || !enabled) {
      return;
    }
    editor.on("update", scheduleSave);
    return () => {
      editor.off("update", scheduleSave);
    };
  }, [editor]);

  return { saveState, scheduleSave, flushSave };
};

export default useDraftAutosave;
